import type { RevenueAnalyticsModel } from '.';
import { parseRevenueAnalyticsProviderIds, withRevenueAnalyticsProviderIds } from './providerSearchParams';
import {
    applySymbolSearch as applySymbolSearchAction,
    clearColumnFilters as clearColumnFiltersAction,
    clearSymbolSearch as clearSymbolSearchAction,
    endCsvDownload as endCsvDownloadAction,
    failCsvDownload as failCsvDownloadAction,
    initialRevenueAnalyticsState,
    revenueAnalyticsReducer,
    selectProviders as selectProvidersAction,
    setFirm as setFirmAction,
    resetColumnVisibility as resetColumnVisibilityAction,
    REVENUE_ANALYTICS_FILTER_COLUMNS,
    setColumnFilter as setColumnFilterAction,
    setPage as setPageAction,
    setRowsPerPage as setRowsPerPageAction,
    setSymbolSearchInput as setSymbolSearchInputAction,
    startCsvDownload as startCsvDownloadAction,
    toggleColumnVisibility as toggleColumnVisibilityAction,
    toggleSort as toggleSortAction,
} from './state';
import type {
    RevenueAnalyticsAction,
    RevenueAnalyticsColumnFilters,
    RevenueAnalyticsFilterColumn,
    RevenueAnalyticsRowsPerPage,
    RevenueAnalyticsSort,
    RevenueAnalyticsSortColumn,
    RevenueAnalyticsState,
} from './state';
import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { Provider } from '@thinkalpha/ui-types/types/admin/lab';
import { t } from 'i18next';
import { optional } from 'inversify';
import { downloadCsvRows } from 'src/admin/LAB/csv/downloadCsv';
import { log } from 'src/admin/LAB/log';
import { numericColumnFilterToOData, parseNumericColumnFilter } from 'src/admin/LAB/RevenueAnalytics/columnFilters';
import {
    sanitizeRevenueAnalyticsColumnVisibility,
    visibleRevenueAnalyticsColumns,
} from 'src/admin/LAB/RevenueAnalytics/columns';
import type {
    RevenueAnalyticsColumn,
    RevenueAnalyticsColumnKey,
    RevenueAnalyticsColumnVisibility,
} from 'src/admin/LAB/RevenueAnalytics/columns';
import { buildRevenueAnalyticsCsvRows } from 'src/admin/LAB/RevenueAnalytics/revenueAnalyticsCsv';
import type { RevenueAnalyticsRawRow, RevenueAnalyticsRow } from 'src/admin/LAB/RevenueAnalytics/types';
import { inject, injectable } from 'src/features/ioc';
import { readStoredColumnVisibility, writeStoredColumnVisibility } from 'src/lib/columnVisibilityStorage';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import {
    getRevenueAnalyticsQuery,
    LAB_REVENUE_ANALYTICS,
    revenueAnalyticsProviderKey,
} from 'src/queries/labRevenueAnalytics';
import type { NavigationService } from 'src/services/NavigationService';
import type { ReactBindings } from 'src/types/bindings';

/** Backend page size for CSV export; /v1/orders/summary/by-symbol rejects a `$top` greater than 250. */
const CSV_EXPORT_PAGE_SIZE = 250;
/** Hard cap on exported rows so a huge result set cannot request an unbounded export. */
const CSV_EXPORT_MAX_ROWS = 10_000;
/** localStorage key for the column show/hide preference (local to this browser, like Order Debug). */
const COLUMN_VISIBILITY_STORAGE_KEY = 'revenue-analytics:column-visibility';

/**
 * Loads the persisted visibility, sanitised against the registry. When a
 * stored map exists but no longer matches (a retired column, a newly added
 * one), the sanitised map is written back so storage does not keep stale
 * keys; an empty store is left alone so defaults are never persisted.
 */
function hydrateColumnVisibility(): RevenueAnalyticsColumnVisibility {
    const stored = readStoredColumnVisibility(COLUMN_VISIBILITY_STORAGE_KEY);
    const sanitized = sanitizeRevenueAnalyticsColumnVisibility(stored);
    const storedKeys = Object.keys(stored);
    const matches =
        storedKeys.length === Object.keys(sanitized).length &&
        storedKeys.every((key) => sanitized[key as RevenueAnalyticsColumnKey] === stored[key]);
    if (storedKeys.length > 0 && !matches) {
        writeStoredColumnVisibility(COLUMN_VISIBILITY_STORAGE_KEY, sanitized);
    }
    return sanitized;
}

/** OData row-shape field behind each sortable column. */
const SORT_FIELD_BY_COLUMN = {
    provider: 'providerId',
    symbol: 'symbol',
    qty: 'quantity',
    avgPrice: 'averagePrice',
    totalCost: 'totalCost',
    curPrice: 'currentPrice',
} satisfies Record<RevenueAnalyticsSortColumn, string>;

/** Builds the `$orderby` expression for a sort, or undefined for the backend default. */
const sortToOrderby = (sort: RevenueAnalyticsSort | null): string | undefined =>
    sort ? `${SORT_FIELD_BY_COLUMN[sort.column]} ${sort.direction}` : undefined;

/** OData row-shape field behind each filterable column. */
const FILTER_FIELD_BY_COLUMN = {
    qty: 'quantity',
    avgPrice: 'averagePrice',
    totalCost: 'totalCost',
    curPrice: 'currentPrice',
} satisfies Record<RevenueAnalyticsFilterColumn, string>;

interface CompiledColumnFilters {
    /** Conjunction of every parsable column filter, or undefined when none applies. */
    readonly expression: string | undefined;
    /** Columns whose text did not parse; surfaced to the view and left out of the query. */
    readonly invalid: ReadonlySet<RevenueAnalyticsFilterColumn>;
}

const compileColumnFilters = (filters: RevenueAnalyticsColumnFilters): CompiledColumnFilters => {
    const clauses: string[] = [];
    const invalid = new Set<RevenueAnalyticsFilterColumn>();
    for (const column of REVENUE_ANALYTICS_FILTER_COLUMNS) {
        const text = filters[column];
        if (text === '') continue;
        const parsed = parseNumericColumnFilter(text);
        if (parsed === null) {
            invalid.add(column);
            continue;
        }
        clauses.push(`(${numericColumnFilterToOData(FILTER_FIELD_BY_COLUMN[column], parsed)})`);
    }
    return { expression: clauses.length > 0 ? clauses.join(' and ') : undefined, invalid };
};

@injectable()
export class RevenueAnalyticsModelImpl implements RevenueAnalyticsModel {
    readonly #disposableStack = new DisposableStack();
    readonly #queryClient: QueryClient;
    readonly #currentUser: ReactBindings['CurrentUserModel'];
    readonly #state = signal<RevenueAnalyticsState>({
        ...initialRevenueAnalyticsState,
        columnVisibility: hydrateColumnVisibility(),
    });
    /**
     * Flips once `setFirm` runs. Gates every query so a superuser session
     * cannot fire the unscoped "all firms" fetch during the synchronous
     * window between construction and the route's `setFirm` call.
     */
    readonly #firmScopeSet = signal(false);
    readonly #abort = new AbortController();

    /**
     * Superuser access is not part of the model's scope: it propagates
     * reactively from the current-user model, so a mode toggle flows
     * into the query gates without rebuilding or rescoping anything.
     */
    readonly #superUserMode: ReadonlySignal<boolean>;
    /**
     * The single authorization predicate for reading analytics: a firm
     * scope must be set, and the unscoped (all-firms) view is superuser-
     * only. Gates the queries, their projections (a disabled query still
     * holds warm cache data), and the CSV export alike.
     */
    readonly #authorized: ReadonlySignal<boolean>;

    #dispatch(action: RevenueAnalyticsAction): void {
        const previous = this.#state.value;
        const next = revenueAnalyticsReducer(previous, action);
        this.#state.value = next;
        if (next.columnVisibility !== previous.columnVisibility) {
            if (TRACE) {
                log.trace('Revenue Analytics column visibility changed', {
                    action: action.type,
                    columnVisibility: next.columnVisibility,
                });
            }
            writeStoredColumnVisibility(COLUMN_VISIBILITY_STORAGE_KEY, next.columnVisibility);
        }
    }

    readonly #navigation: NavigationService | null;
    readonly #providersQuery: QueryObserverModel<Provider[], Error, Provider[]>;
    readonly #analyticsQuery: QueryObserverModel<RevenueAnalyticsRawRow[], Error, RevenueAnalyticsRawRow[]>;

    readonly providers: ReadonlySignal<ReadonlyArray<Provider>>;
    readonly providerNamesById: ReadonlySignal<ReadonlyMap<string, string>>;
    readonly selectedProviderIds: ReadonlySignal<ReadonlyArray<string>> = computed(
        () => this.#state.value.selectedProviderIds,
    );
    readonly selectedProviders: ReadonlySignal<ReadonlyArray<Provider>>;
    readonly rows: ReadonlySignal<ReadonlyArray<RevenueAnalyticsRow>>;
    readonly isLoading: ReadonlySignal<boolean>;
    readonly isError: ReadonlySignal<boolean>;
    readonly isFetching: ReadonlySignal<boolean>;
    readonly lastUpdatedAt: ReadonlySignal<number>;
    readonly isDownloadingCsv: ReadonlySignal<boolean> = computed(() => this.#state.value.isDownloadingCsv);
    readonly csvDownloadFailed: ReadonlySignal<boolean> = computed(() => this.#state.value.csvDownloadFailed);
    readonly symbolSearchInput: ReadonlySignal<string> = computed(() => this.#state.value.symbolSearchInput);
    readonly isSearchActive: ReadonlySignal<boolean> = computed(() => this.#state.value.appliedSymbolSearch !== '');
    readonly page: ReadonlySignal<number> = computed(() => this.#state.value.page);
    readonly rowsPerPage: ReadonlySignal<RevenueAnalyticsRowsPerPage> = computed(() => this.#state.value.rowsPerPage);
    readonly sort: ReadonlySignal<RevenueAnalyticsSort | null> = computed(() => this.#state.value.sort);
    readonly columnFilters: ReadonlySignal<RevenueAnalyticsColumnFilters> = computed(
        () => this.#state.value.columnFilters,
    );
    readonly #compiledColumnFilters: ReadonlySignal<CompiledColumnFilters> = computed(() =>
        compileColumnFilters(this.#state.value.columnFilters),
    );
    readonly invalidColumnFilters: ReadonlySignal<ReadonlySet<RevenueAnalyticsFilterColumn>> = computed(
        () => this.#compiledColumnFilters.value.invalid,
    );
    readonly hasColumnFilters: ReadonlySignal<boolean> = computed(() =>
        REVENUE_ANALYTICS_FILTER_COLUMNS.some((column) => this.#state.value.columnFilters[column] !== ''),
    );
    readonly hasAppliedColumnFilters: ReadonlySignal<boolean> = computed(
        () => this.#compiledColumnFilters.value.expression !== undefined,
    );
    readonly columnVisibility: ReadonlySignal<RevenueAnalyticsColumnVisibility> = computed(
        () => this.#state.value.columnVisibility,
    );
    readonly visibleColumns: ReadonlySignal<ReadonlyArray<RevenueAnalyticsColumn>> = computed(() =>
        visibleRevenueAnalyticsColumns(this.#state.value.columnVisibility),
    );
    readonly hasPreviousPage: ReadonlySignal<boolean>;
    readonly hasNextPage: ReadonlySignal<boolean>;
    readonly pageStartIndex: ReadonlySignal<number>;
    readonly pageEndIndex: ReadonlySignal<number>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: QueryClient,
        @inject('CurrentUserModel') currentUser: ReactBindings['CurrentUserModel'],
        @inject('NavigationService') @optional() navigation?: NavigationService,
    ) {
        this.#queryClient = queryClient;
        this.#currentUser = currentUser;
        // Initialized here, after `#currentUser` is assigned, rather than as
        // field initializers that would depend on `computed` staying lazy.
        this.#superUserMode = computed(
            () => this.#currentUser.isSuperuser.value && this.#currentUser.isSuperUserModeEnabled.value,
        );
        this.#authorized = computed(
            () => this.#firmScopeSet.value && (this.#superUserMode.value || Boolean(this.#state.value.firmId)),
        );
        this.#navigation = navigation ?? null;

        // The URL is the source of truth for the provider selection: `providerId`
        // params flow into state here, and `selectProviders` writes them, so a
        // pasted link, back/forward, and the filter all resolve the same way.
        if (navigation) {
            this.#disposableStack.defer(
                effect(() => {
                    const providerIds = parseRevenueAnalyticsProviderIds(navigation.location.value.search);
                    untracked(() => this.#applyProviderSelection(providerIds));
                }),
            );
        }

        // Fetched unscoped: the analytics rows are scoped by the caller's
        // access, not the viewed firm, so they can reference providers other
        // firms own — whose names must still resolve. The firm scope is
        // applied client-side to the picker list below (the same
        // `provider.firmId` match the backend's `firmId` param performs).
        const providersOptions = computed<QueryObserverOptions<Provider[], Error, Provider[]>>(() => {
            return {
                ...getAllProvidersQuery(),
                enabled: this.#authorized.value,
            };
        });
        this.#providersQuery = queryObserverModelFactory<Provider[], Error, Provider[]>(providersOptions);
        this.#disposableStack.use(this.#providersQuery);

        // Gate the projection too: `enabled: false` only blocks fetching, a
        // warm cache would still surface the unscoped all-firms list before
        // `setFirm` runs or after superuser mode is switched off.
        const accessibleProviders = computed(() =>
            this.#authorized.value ? (this.#providersQuery.signal.value.data ?? []) : [],
        );
        this.providerNamesById = computed(
            () => new Map(accessibleProviders.value.map((provider): [string, string] => [provider.id, provider.name])),
        );
        this.providers = computed(() => {
            const firmId = this.#state.value.firmId;
            const all = accessibleProviders.value;
            return firmId ? all.filter((provider) => provider.firmId === firmId) : all;
        });
        this.selectedProviders = computed(() => {
            const ids = this.#state.value.selectedProviderIds;
            return this.providers.value.filter((provider) => ids.includes(provider.id));
        });

        const analyticsOptions = computed<
            QueryObserverOptions<RevenueAnalyticsRawRow[], Error, RevenueAnalyticsRawRow[]>
        >(() => {
            const s = this.#state.value;
            // No selection loads the aggregate across all providers; ids
            // narrow to those. Gate on scope like the providers query so the
            // query doesn't run before a firm/superuser context is known.
            const providerIds = s.selectedProviderIds.length > 0 ? s.selectedProviderIds : undefined;
            const filter = this.#compiledColumnFilters.value.expression;
            const enabled = this.#authorized.value;
            // A committed search becomes a `symbol eq …` clause in the `$filter`.
            // Paging is bypassed (one row per matching provider at most), but
            // the sort and column filters still apply so the aggregate view
            // stays orderable and narrowable.
            if (s.appliedSymbolSearch) {
                return {
                    ...getRevenueAnalyticsQuery({
                        providerIds,
                        filter,
                        symbol: s.appliedSymbolSearch,
                        orderby: sortToOrderby(s.sort),
                    }),
                    enabled,
                };
            }
            return {
                ...getRevenueAnalyticsQuery({
                    providerIds,
                    filter,
                    orderby: sortToOrderby(s.sort),
                    skip: (s.page - 1) * s.rowsPerPage,
                    top: s.rowsPerPage,
                }),
                enabled,
            };
        });
        this.#analyticsQuery = queryObserverModelFactory<RevenueAnalyticsRawRow[], Error, RevenueAnalyticsRawRow[]>(
            analyticsOptions,
        );
        this.#disposableStack.use(this.#analyticsQuery);

        // Pair each raw row with its provider's display name; the aggregate
        // view spans providers, so names are resolved here rather than baked
        // in at fetch time. Falls back to the id if the name isn't loaded yet.
        this.rows = computed<RevenueAnalyticsRow[]>(() => {
            // `enabled: false` only blocks fetching; warm cache data must not
            // flow through without authorization.
            if (!this.#authorized.value) return [];
            const raw = this.#analyticsQuery.signal.value.data ?? [];
            const nameById = this.providerNamesById.value;
            return raw.map((row) => ({ ...row, providerName: nameById.get(row.providerId) ?? row.providerId }));
        });
        this.isLoading = computed(() => this.#analyticsQuery.signal.value.isLoading);
        this.isError = computed(() => this.#analyticsQuery.signal.value.isError);
        this.isFetching = computed(() => this.#analyticsQuery.signal.value.isFetching);
        this.lastUpdatedAt = computed(() => this.#analyticsQuery.signal.value.dataUpdatedAt);

        this.hasPreviousPage = computed(() => !this.isSearchActive.value && this.#state.value.page > 1);
        // No total count from the backend yet (a follow-up will add it): a full
        // page implies there may be more, so Next is enabled only when the
        // current page came back full.
        this.hasNextPage = computed(
            () => !this.isSearchActive.value && this.rows.value.length === this.#state.value.rowsPerPage,
        );
        this.pageStartIndex = computed(() => {
            if (this.rows.value.length === 0) return 0;
            if (this.isSearchActive.value) return 1;
            const s = this.#state.value;
            return (s.page - 1) * s.rowsPerPage + 1;
        });
        this.pageEndIndex = computed(() => {
            const count = this.rows.value.length;
            if (count === 0) return 0;
            if (this.isSearchActive.value) return count;
            const s = this.#state.value;
            return (s.page - 1) * s.rowsPerPage + count;
        });
    }

    setFirm(firmId: string | undefined): void {
        if (this.#firmScopeSet.value) {
            if (this.#state.value.firmId === firmId) return;
            throw new Error(
                'RevenueAnalyticsModel firm scope is fixed after initialization; create a new instance for a different firm',
            );
        }
        // Dispatch first so the queries see the scoped firm and the gate
        // flip in one consistent read.
        this.#dispatch(setFirmAction(firmId));
        this.#firmScopeSet.value = true;
    }

    selectProviders(providerIds: ReadonlyArray<string>): void {
        const navigation = this.#navigation;
        if (!navigation) {
            this.#applyProviderSelection(providerIds);
            return;
        }
        // Replace, not push: toggling filters should not pile up history entries.
        const location = navigation.location.value;
        void navigation.navigate(
            {
                pathname: location.pathname,
                hash: location.hash,
                search: withRevenueAnalyticsProviderIds(location.search, providerIds),
            },
            { replace: true },
        );
    }

    #applyProviderSelection(providerIds: ReadonlyArray<string>): void {
        const previous = this.#state.value.selectedProviderIds;
        this.#dispatch(selectProvidersAction(providerIds));
        if (this.#state.value.selectedProviderIds !== previous) {
            if (TRACE) {
                log.trace('Revenue Analytics provider selection changed', {
                    providerIds: this.#state.value.selectedProviderIds,
                });
            }
        }
    }

    setRowsPerPage(rowsPerPage: RevenueAnalyticsRowsPerPage): void {
        this.#dispatch(setRowsPerPageAction(rowsPerPage));
    }

    toggleSort(column: RevenueAnalyticsSortColumn): void {
        this.#dispatch(toggleSortAction(column));
    }

    setColumnFilter(column: RevenueAnalyticsFilterColumn, value: string): void {
        this.#dispatch(setColumnFilterAction(column, value));
    }

    clearColumnFilters(): void {
        this.#dispatch(clearColumnFiltersAction());
    }

    toggleColumnVisibility(column: RevenueAnalyticsColumnKey): void {
        this.#dispatch(toggleColumnVisibilityAction(column));
    }

    resetColumnVisibility(): void {
        this.#dispatch(resetColumnVisibilityAction());
    }

    goToPreviousPage(): void {
        if (this.hasPreviousPage.value) this.#dispatch(setPageAction(this.#state.value.page - 1));
    }

    goToNextPage(): void {
        if (this.hasNextPage.value) this.#dispatch(setPageAction(this.#state.value.page + 1));
    }

    setSymbolSearchInput(value: string): void {
        this.#dispatch(setSymbolSearchInputAction(value));
    }

    applySymbolSearch(): void {
        this.#dispatch(applySymbolSearchAction());
    }

    clearSymbolSearch(): void {
        this.#dispatch(clearSymbolSearchAction());
    }

    async refresh(): Promise<void> {
        const providerKey = revenueAnalyticsProviderKey(this.#state.value.selectedProviderIds);
        await this.#queryClient.invalidateQueries({ queryKey: [LAB_REVENUE_ANALYTICS, providerKey] });
    }

    async downloadCsv(): Promise<void> {
        if (this.#abort.signal.aborted) return;
        // The export fetches directly (bypassing `enabled`), so it applies
        // the queries' own authorization gate itself.
        if (!this.#authorized.value) return;
        if (typeof document === 'undefined') return;
        // Guard re-entrancy: the button also disables on this flag, but a
        // keyboard-repeat or programmatic caller could still fire twice.
        if (this.#state.value.isDownloadingCsv) return;
        this.#dispatch(startCsvDownloadAction());
        try {
            const s = this.#state.value;
            const providerIds = s.selectedProviderIds.length > 0 ? s.selectedProviderIds : undefined;
            const filter = this.#compiledColumnFilters.value.expression;
            const raw: RevenueAnalyticsRawRow[] = [];
            const orderby = sortToOrderby(s.sort);
            if (s.appliedSymbolSearch) {
                // A committed search returns its matches in one page; export just those.
                const matches = await this.#fetchAnalytics({
                    providerIds,
                    filter,
                    symbol: s.appliedSymbolSearch,
                    orderby,
                });
                // Authorization is reactive; it may have been revoked mid-fetch.
                if (this.#abort.signal.aborted || !this.#authorized.value) return;
                raw.push(...matches);
            } else {
                // The user expects the full result set, not just the current
                // page. Walk the backend pages (no total count is reported)
                // until a short page, capped so an unexpectedly huge result
                // set cannot request an unbounded export. The active sort is
                // carried so the file reads in the on-screen order.
                let skip = 0;
                for (;;) {
                    const page = await this.#fetchAnalytics({
                        providerIds,
                        filter,
                        orderby,
                        skip,
                        top: CSV_EXPORT_PAGE_SIZE,
                    });
                    if (this.#abort.signal.aborted || !this.#authorized.value) return;
                    raw.push(...page);
                    if (page.length < CSV_EXPORT_PAGE_SIZE || raw.length >= CSV_EXPORT_MAX_ROWS) break;
                    skip += CSV_EXPORT_PAGE_SIZE;
                }
            }
            if (raw.length === 0) return;
            const nameById = this.providerNamesById.value;
            const rows = raw
                .slice(0, CSV_EXPORT_MAX_ROWS)
                .map((row) => ({ ...row, providerName: nameById.get(row.providerId) ?? row.providerId }));
            downloadCsvRows(
                'revenue-analytics',
                buildRevenueAnalyticsCsvRows(rows, {
                    provider: t('admin::lab::revenue-analytics::col-provider', 'Provider'),
                    symbol: t('admin::lab::revenue-analytics::col-symbol', 'Symbol'),
                    qty: t('admin::lab::revenue-analytics::col-qty', 'Qty'),
                    avgPrice: t('admin::lab::revenue-analytics::col-avg-price', 'Avg Price'),
                    totalCost: t('admin::lab::revenue-analytics::col-total-cost', 'Total Cost'),
                    curPrice: t('admin::lab::revenue-analytics::col-cur-price', 'Cur Price'),
                    fairValue: t('admin::lab::revenue-analytics::col-fair-value', 'Fair Value'),
                    sodInventory: t('admin::lab::revenue-analytics::col-sod-inv', 'SOD Inv'),
                    remainingInventory: t('admin::lab::revenue-analytics::col-rem-inv', 'Rem Inv'),
                    percentUsed: t('admin::lab::revenue-analytics::col-pct-used', '% Used'),
                    percentRemaining: t('admin::lab::revenue-analytics::col-pct-rem', '% Rem'),
                }),
            );
        } catch (error) {
            log.error('Revenue Analytics CSV export failed', { error });
            if (!this.#abort.signal.aborted) this.#dispatch(failCsvDownloadAction());
        } finally {
            // A disposed model's state is dead; nothing may write to it.
            if (!this.#abort.signal.aborted) this.#dispatch(endCsvDownloadAction());
        }
    }

    #fetchAnalytics(options: Parameters<typeof getRevenueAnalyticsQuery>[0]): Promise<RevenueAnalyticsRawRow[]> {
        const queryOptions = getRevenueAnalyticsQuery(options);
        return this.#queryClient.fetchQuery({ ...queryOptions, queryKey: queryOptions.queryKey ?? [] });
    }

    [Symbol.dispose](): void {
        this.#abort.abort();
        this.#disposableStack.dispose();
    }
}
